// View 4: Personal Information

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useForm, Controller, useWatch } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import clsx from 'clsx';
import { PhoneInput, DatePicker, DOBInput } from '@/components/forms';
import { useOrderStore } from '@/stores/orderStore';
import { useFlowNavigation } from '@/hooks';
import { submitGetStarted } from '@/services/userService';
import { syncCartWithSelections } from '@/services/cartService';
import { track } from '@/utils/analytics';
import { logger } from '@/utils/logger';
import { validateDOB } from '@/utils/dateValidation';
import type { PersonalInfo } from '@/types';

// Helper to check if a field is filled (for field-filled class)
const isFilled = (value: string | undefined | null): boolean => {
  return typeof value === 'string' && value.trim() !== '';
};

const personalInfoSchema = z.object({
  firstName: z.string().min(1, 'Required'),
  lastName: z.string(),
  email: z.string().email('Invalid email address'),
  phone: z.string().min(10, 'Phone number is required'),
  moveDate: z.string().min(1, 'Move-in date is required'),
  dob: z.string().min(1, 'Date of birth is required').superRefine((val, ctx) => {
    if (!val) return;
    const result = validateDOB(val);
    if (!result.valid) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, message: result.message });
    }
  }),
});

type PersonalInfoForm = z.infer<typeof personalInfoSchema>;

export function PersonalInfoView() {
  const navigate = useNavigate();
  const { userId, address, propertyData, personalInfo, setPersonalInfo, selectedServices, setPlanOptionsQueue } = useOrderStore();
  const { navigateFromPersonalInfo, flowType } = useFlowNavigation();

  // Initialize toggle states from stored values
  const [rentOrOwn, setRentOrOwn] = useState<'Rent' | 'Own' | null>(
    personalInfo?.rentOrOwn || null
  );
  const [propertyType, setPropertyType] = useState<'Residential' | 'Business'>(
    personalInfo?.propertyType || 'Residential'
  );
  const [submitError, setSubmitError] = useState<string | null>(null);

  const {
    register,
    control,
    handleSubmit,
    formState: { errors, isSubmitting, isValid },
  } = useForm<PersonalInfoForm>({
    resolver: zodResolver(personalInfoSchema),
    mode: 'onChange', // Enable real-time validation for isValid
    // Restore form values from store for better back navigation UX
    defaultValues: {
      firstName: personalInfo?.businessName || personalInfo?.firstName || '',
      lastName: personalInfo?.lastName || '',
      email: personalInfo?.email || '',
      phone: personalInfo?.phone || '',
      moveDate: personalInfo?.moveDate || '',
      dob: personalInfo?.dob || '',
    },
  });

  // Watch all values for field-filled styling
  const watchedValues = useWatch({ control });

  const onSubmit = async (data: PersonalInfoForm) => {
    setSubmitError(null);

    // Require rent/own selection
    if (!rentOrOwn) {
      setSubmitError('Please select whether you rent or own.');
      return;
    }

    // Require lastName for residential
    if (propertyType !== 'Business' && !data.lastName?.trim()) {
      setSubmitError('Last name is required.');
      return;
    }

    let firstName = data.firstName;
    let lastName = data.lastName;
    let businessName: string | undefined;

    if (propertyType === 'Business') {
      // Split business name: last word → lastName, rest → firstName
      businessName = data.firstName.trim();
      const words = businessName.split(/\s+/);
      lastName = words.length > 1 ? words.pop()! : '';
      firstName = words.join(' ');
    }

    const personalInfo: PersonalInfo = {
      ...data,
      firstName,
      lastName,
      rentOrOwn,
      propertyType,
      ...(propertyType === 'Business' ? { businessName } : {}),
    };

    // Store in Zustand
    setPersonalInfo(personalInfo);

    // Submit to API if we have required data
    if (userId && address) {
      const result = await submitGetStarted(userId, address, personalInfo, propertyData);
      if (!result.success) {
        setSubmitError(result.error || 'Failed to submit information. Please try again.');
        logger.error({ area: 'personal-info' }, 'Personal info submission failed: ' + (result.error || 'unknown'));
        track('checkout_error', {
          error_context: 'personal_info_submit',
          error_message: result.error || 'Unknown error',
        });
        return;
      }

      // For Flow B: sync cart after get-started creates it
      if (flowType === 'B' && selectedServices.length > 0) {
        const cartSelections = selectedServices.map((s) => ({
          planId: s.planId,
          vendorId: s.vendorId,
          serviceName: s.type.charAt(0).toUpperCase() + s.type.slice(1),
          options: s.options,
        }));
        await syncCartWithSelections(userId, cartSelections);
      }
    }

    // For Flow B: set up plan options queue with any selected services
    // (In Flow B, utilities are selected before personal info)
    const planIds = selectedServices.map((s) => s.planId);
    if (planIds.length > 0) {
      setPlanOptionsQueue(planIds);
    }

    track('checkout_personal_info_completed', {
      zip: address?.zip,
      flow_type: flowType,
      rent_or_own: rentOrOwn,
      property_type: propertyType,
    });

    // Use flow-aware navigation
    // Flow A: goes to /utilities
    // Flow B: goes to /plan-options/{planId} or /checkout
    navigateFromPersonalInfo(planIds);
  };

  // Get tomorrow's date as minimum, 90 days out as maximum
  const tomorrow = new Date()
  tomorrow.setDate(tomorrow.getDate() + 1)
  const minDate = tomorrow.toISOString().split('T')[0]
  const maxDateObj = new Date()
  maxDateObj.setDate(maxDateObj.getDate() + 90)
  const maxDate = maxDateObj.toISOString().split('T')[0]

  return (
    <section className="view" style={{ display: 'block' }}>
      <div className="container">
        <h1 className="text--center mb-2">Tell us about yourself</h1>
        <p className="text--center mb-4">We'll use this to set up services</p>

        <form onSubmit={handleSubmit(onSubmit)}>
          {/* Property Type Toggle */}
          <div className="form-group">
            <label className="form-label">Property Type</label>
            <div className="toggle-group">
              <button
                type="button"
                className={`toggle-btn ${propertyType === 'Residential' ? 'active' : ''}`}
                onClick={() => setPropertyType('Residential')}
              >
                Residential
              </button>
              <button
                type="button"
                className={`toggle-btn ${propertyType === 'Business' ? 'active' : ''}`}
                onClick={() => setPropertyType('Business')}
              >
                Business
              </button>
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="firstName" className="form-label">
              <span>
                {propertyType === 'Business' ? 'BUSINESS NAME' : 'FIRST NAME'}{' '}
                <span className={clsx('required', { filled: isFilled(watchedValues.firstName) })}>*</span>
              </span>
            </label>
            <input
              type="text"
              id="firstName"
              {...register('firstName')}
              required
              placeholder={propertyType === 'Business' ? 'Enter business name' : ''}
              className={clsx({ 'field-filled': isFilled(watchedValues.firstName), error: errors.firstName })}
            />
            {errors.firstName && <span className="form-error">{errors.firstName.message}</span>}
          </div>

          {propertyType !== 'Business' && (
            <div className="form-group">
              <label htmlFor="lastName" className="form-label">
                <span>
                  LAST NAME{' '}
                  <span className={clsx('required', { filled: isFilled(watchedValues.lastName) })}>*</span>
                </span>
              </label>
              <input
                type="text"
                id="lastName"
                {...register('lastName')}
                required
                className={clsx({ 'field-filled': isFilled(watchedValues.lastName), error: errors.lastName })}
              />
              {errors.lastName && <span className="form-error">{errors.lastName.message}</span>}
            </div>
          )}

          <div className="form-group">
            <label htmlFor="phone" className="form-label">
              <span>PHONE NUMBER <span className={clsx('required', { filled: isFilled(watchedValues.phone) })}>*</span></span>
            </label>
            <Controller
              name="phone"
              control={control}
              render={({ field }) => (
                <PhoneInput
                  id="phone"
                  value={field.value}
                  onChange={field.onChange}
                  className={clsx({ 'field-filled': isFilled(field.value), error: errors.phone })}
                  required
                />
              )}
            />
            {errors.phone && <span className="form-error">{errors.phone.message}</span>}
          </div>

          <div className="form-group">
            <label htmlFor="email" className="form-label">
              <span>EMAIL ADDRESS <span className={clsx('required', { filled: isFilled(watchedValues.email) })}>*</span></span>
            </label>
            <input
              type="email"
              id="email"
              {...register('email')}
              required
              className={clsx({ 'field-filled': isFilled(watchedValues.email), error: errors.email })}
            />
            {errors.email && <span className="form-error">{errors.email.message}</span>}
          </div>

          <div className="form-group">
            <label htmlFor="dob" className="form-label">
              <span>DATE OF BIRTH <span className={clsx('required', { filled: isFilled(watchedValues.dob) })}>*</span></span>
            </label>
            <Controller
              name="dob"
              control={control}
              render={({ field }) => (
                <DOBInput
                  id="dob"
                  value={field.value}
                  onChange={field.onChange}
                  className={clsx({ 'field-filled': isFilled(field.value), error: errors.dob })}
                  required
                />
              )}
            />
            {errors.dob && <span className="form-error">{errors.dob.message}</span>}
          </div>

          {/* Rent or Own Toggle */}
          <div className="form-group">
            <label className="form-label">
              <span>DO YOU RENT OR OWN? <span className={clsx('required', { filled: !!rentOrOwn })}>*</span></span>
            </label>
            <div className={clsx('toggle-group', { 'field-filled': !!rentOrOwn })}>
              <button
                type="button"
                className={`toggle-btn ${rentOrOwn === 'Rent' ? 'active' : ''}`}
                onClick={() => setRentOrOwn('Rent')}
              >
                Rent
              </button>
              <button
                type="button"
                className={`toggle-btn ${rentOrOwn === 'Own' ? 'active' : ''}`}
                onClick={() => setRentOrOwn('Own')}
              >
                Own
              </button>
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="moveDate" className="form-label">
              <span>WHAT IS YOUR EXPECTED MOVE IN DATE? <span className={clsx('required', { filled: isFilled(watchedValues.moveDate) })}>*</span></span>
            </label>
            <Controller
              name="moveDate"
              control={control}
              render={({ field }) => (
                <DatePicker
                  id="moveDate"
                  value={field.value}
                  onChange={field.onChange}
                  minDate={minDate}
                  maxDate={maxDate}
                  className={clsx({ error: errors.moveDate })}
                  required
                />
              )}
            />
            {errors.moveDate && <span className="form-error">{errors.moveDate.message}</span>}
          </div>

          {submitError && (
            <div className="form-error-banner mb-3">
              {submitError}
            </div>
          )}

          <p className="consent-disclosure">
            By clicking &lsquo;Continue&rsquo; you provide your consent and agree 2TurnItOn may contact
            you about utilities and other home services via phone, SMS, and email provided above, and
            also agree to our{' '}
            <a href="https://www.2turniton.com/terms-conditions" target="_blank" rel="noopener noreferrer">
              Terms and Conditions
            </a>{' '}
            and{' '}
            <a href="https://www.2turniton.com/privacy-policy" target="_blank" rel="noopener noreferrer">
              Privacy Policy
            </a>
            . Message and data rates may apply. Message frequency varies. Text STOP to cancel and HELP
            for help.
          </p>

          <div className="action-row">
            <button type="button" className="btn btn--back" onClick={() => navigate(-1)}>
              GO BACK
            </button>
            <button type="submit" className="btn" disabled={isSubmitting || !isValid || !rentOrOwn}>
              {isSubmitting ? 'SAVING...' : 'CONTINUE'}
            </button>
          </div>
        </form>
      </div>
    </section>
  )
}
